import { ReactQueryV5 } from "@mindlercare/mindlerapi-client";
const { useMutation, useQuery, useQueryClient } = ReactQueryV5;
import { parseISO } from "date-fns";
import { del, get, post, put } from "../services/httpClient";
import { logger } from "core/logger";
import { getCountryCode } from "helpers/getCountryCode";
import {
  concatQueryParams,
  isForbiddenError,
  isErrorWithStatus,
} from "./helpers";
import type {
  FeedbackDraftStatusResponse,
  GenerateFeedbackDraftRequest,
  GenerateFeedbackDraftResponse,
  GetAllFeedbackDraftsRequest,
  GetAllFeedbackDraftsResponse,
  IcbtAssessment,
  IcbtClaims,
  IcbtCommunication,
  IcbtFeedback,
  IcbtRequest,
  IcbtResponse,
  PhraseLibraryResponse,
  IcbtTasks,
  Progress,
  ProgressByProgramId,
  PsychologistSlots,
  PutIcbtAssessmentResponse,
  SequentialUnlockItem,
} from "./types";

function toCamelCase(str: string): string {
  return str.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function camelcaseKeys<T extends Record<string, any>>(obj: T): T {
  if (Array.isArray(obj)) {
    return obj.map(camelcaseKeys) as unknown as T;
  } else if (obj !== null && typeof obj === "object") {
    return Object.fromEntries(
      Object.entries(obj).map(([key, value]) => [
        toCamelCase(key),
        camelcaseKeys(value),
      ]),
    ) as T;
  }
  return obj;
}

const ICBT_STALE_TIME = 5 * 60_000;
const ICBT_STALE_TIME_SHORT = 60_000;

const ICBT_CLAIMS = "icbtClaims";
const ICBT_REQUESTS = "icbtRequests";
const ICBT_PROGRESS = "icbtProgress";
const ICBT_PROGRESS_FOR_USERS = "icbtProgressForUsers";
const ICBT_COMMUNICATION = "icbtCommunication";
const ICBT_PSYCHOLOGIST_COMMUNICATION = "icbtPsychologistCommunication";
const ICBT_PSYCHOLOGIST_TASKS = "icbtPsychologistTasks";
const ICBT_RESPONSES = "icbtResponses";
const ICBT_FEEDBACKS = "icbtFeedbacks";
const ICBT_NEXT_SLOT_AND_FEEDBACK = "icbtNextSlotAndFeedback";
const ICBT_PSYCHOLOGIST_REQUESTS = "icbtPsychologistRequests";
const ICBT_SEQUENTIAL_UNLOCK = "icbtSequentialUnlock";
const ICBT_FEEDBACK_DRAFT_STATUS = "icbtFeedbackDraftStatus";
const ICBT_FEEDBACK_DRAFTS_BY_REQUEST = "icbtFeedbackDraftsByRequestId";
const ICBT_ASSESSMENT = "icbtAssessment";
const ICBT_PHRASE_LIBRARY = "icbt-phrase-library";

export const icbtKeys = {
  claims: (args: { userId?: number; marketISO2?: string }) => [
    ICBT_CLAIMS,
    args,
  ],
  requests: (userId: number | undefined) => [ICBT_REQUESTS, userId],
  progress: (userId: string | undefined) => [ICBT_PROGRESS, userId],
  progressForUsers: (userIds: string[]) => [ICBT_PROGRESS_FOR_USERS, userIds],
  communication: (userId: number | undefined) => [ICBT_COMMUNICATION, userId],
  psychologistCommunication: (
    args: { startDate: Date; endDate: Date } | undefined,
  ) => [ICBT_PSYCHOLOGIST_COMMUNICATION, args],
  responses: (args: { userId: number; icbtRequestId: string }) => [
    ICBT_RESPONSES,
    args,
  ],
  psychologistTasks: (
    args: { status: "todo" | "done" | undefined } | undefined,
  ) => [ICBT_PSYCHOLOGIST_TASKS, args],

  feedbacks: (args: { userId: number; icbtRequestId: string }) => [
    ICBT_FEEDBACKS,
    args,
  ],
  nextSlotAndFeedback: (userId: string) => [
    ICBT_NEXT_SLOT_AND_FEEDBACK,
    userId,
  ],
  psychologistRequestsByDateRange: (args: unknown) => [
    ICBT_PSYCHOLOGIST_REQUESTS,
    args,
  ],
  sequentialUnlock: (userId: string) => [ICBT_SEQUENTIAL_UNLOCK, userId],
  feedbackDraftStatus: (draftId: string | null) => [
    ICBT_FEEDBACK_DRAFT_STATUS,
    draftId,
  ],
  feedbackDraftsByRequestId: (args: unknown) => [
    ICBT_FEEDBACK_DRAFTS_BY_REQUEST,
    args,
  ],
  assessment: (args: { icbtRequestId: string }) => [ICBT_ASSESSMENT, args],
  phraseLibrary: (libraryId: string) => [ICBT_PHRASE_LIBRARY, libraryId],
};

export const useInvalidateIcbtClaims = () => {
  const queryClient = useQueryClient();
  return () => {
    queryClient.invalidateQueries({ queryKey: [ICBT_CLAIMS] });
  };
};

export const useInvalidateIcbtRequests = () => {
  const queryClient = useQueryClient();
  return (userId: number | undefined) => {
    queryClient.invalidateQueries({ queryKey: icbtKeys.requests(userId) });
    queryClient.invalidateQueries({
      queryKey: icbtKeys.progress(String(userId)),
    });
  };
};

export const useGetIcbtClaims = (
  args:
    | { userId: number | undefined; marketISO2?: string | undefined }
    | undefined,
  options?: { enabled?: boolean },
) =>
  useQuery({
    queryKey: icbtKeys.claims(args ?? {}),
    queryFn: async () =>
      await post<IcbtClaims, typeof args>("indianapolis/icbt-claims", args),
    staleTime: Infinity,
    ...options,
  });

export const useGetIcbtRequests = (
  userId: number | undefined,
  options?: { enabled?: boolean },
) =>
  useQuery({
    queryKey: icbtKeys.requests(userId),
    queryFn: async () => {
      const res = await get<{ data: IcbtRequest[] }>(
        concatQueryParams("privacy/data-collection/icbt_requests", {
          userId: userId!,
        }),
      );
      return res.data;
    },
    enabled: userId !== undefined && (options?.enabled ?? true),
    staleTime: ICBT_STALE_TIME,
  });

// TEMP BACKEND STAND-IN — the publish job that fills the real endpoint isn't
// merged yet, so this mock lets product try the phrase-library UI on this
// feature branch. Swap the queryFn below back to the real fetch (and delete
// this mock) once the backend lands.
const PHRASE_LIBRARY_MOCK: PhraseLibraryResponse = {
  categories: [
    { key: "assessment", label: "Bedömningsfas", order: 1, color: "blue" },
    { key: "treatment", label: "Behandlingsfas", order: 2, color: "purple" },
    { key: "closing", label: "Avslutningsfas", order: 3, color: "orange" },
  ],
  phrases: [
    {
      id: "p1",
      categoryKey: "assessment",
      sortOrder: 10,
      title: "Generell IKBT bedömning",
      body: "Utifrån samlad symptombild och förutsättningar bedöms patienten tillgodogöra sig KBT-behandling inom primärvårdens ramar i digitalt format. Återkopplar och öppnar IKBT modul.",
    },
    {
      id: "p2",
      categoryKey: "assessment",
      sortOrder: 20,
      title: "IKBT stress",
      body: "Sammantagen klinisk bild förenlig med stressrelaterad problematik. Motiverat med psykologisk intervention, stresshantering, inom primärvårdskontext, digital vårdkontakt. Återkopplar och tilldelar nytt IKBT-program i enlighet med behandlingsplanering.",
    },
    {
      id: "p3",
      categoryKey: "assessment",
      sortOrder: 30,
      title: "Exempel IKBT oro/ångest",
      body: "Sammantaget en symtombild förenlig med ångest och oro. Fortsatt psykologisk intervention, KBT, på primärvårdsnivå bedöms indicerad. Digital vårdkontakt. Återkopplar och tilldelar IKBT-modul enligt behandlingsplan.",
    },
    {
      id: "p4",
      categoryKey: "assessment",
      sortOrder: 40,
      title: "Exempel IKBT nedstämdhet",
      body: "Sammantaget en besvärsbild med nedstämdhet som motiverar KBT-behandling. Utifrån förutsättningar och motivation bedöms digital vårdkontakt inom primärvårdens ramar lämpligt. Återkopplar och tilldelar IKBT-program.",
    },
    {
      id: "p5",
      categoryKey: "treatment",
      sortOrder: 10,
      title: "Generell IKBT behandling",
      body: "Aktivt arbete med tilldelad IKBT. Utifrån patientens reflektioner framkommer inga tecken på försämring. Formatet bedöms fortsatt lämpligt och nästa modul tilldelas enligt plan.",
    },
    {
      id: "p6",
      categoryKey: "treatment",
      sortOrder: 20,
      title: "IKBT stress",
      body: "Stresshantering är fortsatt motiverat utifrån patientens symptombild och förutsättningar. Formatet bedöms fortsatt lämpligt. Återkopplar och tilldelar nästa modul.",
    },
    {
      id: "p7",
      categoryKey: "treatment",
      sortOrder: 30,
      title: "IKBT oro/ångest",
      body: "Sammantagen klinisk bild som motiverar fortsatt KBT-behandling enligt behandlingsplanering. Aktivt arbete med tilldelad IKBT. Utifrån patientens reflektioner framkommer inga tecken på försämring. Digital vårdkontakt inom primärvårdskontext. Återkopplar och tilldelar nytt IKBT-program.",
    },
    {
      id: "p8",
      categoryKey: "treatment",
      sortOrder: 40,
      title: "IKBT nedstämdhet",
      body: "Fortsatt psykologisk behandling enligt behandlingsplan bedöms vara indicerad utifrån sammantagen besvärsbild och förutsättningar. Digital vårdkontakt på primärvårdsnivå. Återkopplar och tilldelar nytt IKBT-program.",
    },
    {
      id: "p9",
      categoryKey: "closing",
      sortOrder: 10,
      title: "IKBT",
      body: "Sammantaget är bedömningen att det finns behov av arbete med vidmakthållandeplan och återfallsprevention. Återkopplar och tilldelar IKBT-program för vidmakthållande.",
    },
  ],
};

export const useGetPhraseLibrary = (
  libraryId: string,
  options?: { enabled?: boolean },
) =>
  useQuery({
    queryKey: icbtKeys.phraseLibrary(libraryId),
    // TEMP: serve the mock until the backend endpoint is merged; then delete
    // this line and restore the real fetch below.
    queryFn: async () => PHRASE_LIBRARY_MOCK,
    // queryFn: async () =>
    //   await get<PhraseLibraryResponse>(
    //     `indianapolis/phraselibrary/${libraryId}/phrases`,
    //   ),
    staleTime: ICBT_STALE_TIME, // published reference content; 5 min is plenty
    ...options,
  });

export const useGetAllProgress = (
  userId: string | undefined,
  options?: { enabled?: boolean },
) =>
  useQuery({
    queryKey: icbtKeys.progress(userId),
    queryFn: async () => {
      try {
        return await post<ProgressByProgramId, { userId: string }>(
          "indianapolis/progress/internal",
          { userId: userId! },
        );
      } catch (e) {
        if (isForbiddenError(e) || isErrorWithStatus(e, 404)) return {};
        throw e;
      }
    },
    enabled: !!userId && (options?.enabled ?? true),
    staleTime: ICBT_STALE_TIME,
  });

export const useGetIcbtResponses = (
  args: { userId: number; icbtRequestId: string },
  options?: { enabled?: boolean },
) =>
  useQuery({
    queryKey: icbtKeys.responses(args),
    queryFn: async () => {
      try {
        const res = await post<
          { data: IcbtResponse | IcbtResponse[] },
          typeof args
        >("health-profiles/internal/icbt-response", args);
        return Array.isArray(res.data) ? res.data : [res.data];
      } catch (e) {
        if (isErrorWithStatus(e, 404)) return [];
        throw e;
      }
    },
    staleTime: ICBT_STALE_TIME,
    ...options,
  });

export const useGetIcbtFeedbacks = (
  args: { userId: number; icbtRequestId: string },
  options?: { enabled?: boolean },
) =>
  useQuery({
    queryKey: icbtKeys.feedbacks(args),
    queryFn: async () => {
      try {
        const res = await post<{ data: IcbtFeedback[] }, typeof args>(
          "health-profiles/internal/icbt-feedbacks",
          args,
        );
        return res.data;
      } catch (e) {
        if (isErrorWithStatus(e, 404)) return [];
        throw e;
      }
    },
    staleTime: ICBT_STALE_TIME,
    ...options,
  });

export const useGetPsychologistIcbtRequestsByDateRange = (
  args: { psychologistUserId: number; fromDate: Date; toDate: Date },
  options?: { enabled?: boolean },
) =>
  useQuery({
    queryKey: icbtKeys.psychologistRequestsByDateRange(args),
    queryFn: async () => {
      const res = await post<{ data: IcbtRequest[] }, typeof args>(
        "health-profiles/interal/icbt_requests/psychologist-date-range",
        args,
      );
      return camelcaseKeys(res.data);
    },
    staleTime: ICBT_STALE_TIME,
    ...options,
  });

// Queues a draft for generation. The backend returns 202 with a draftId; poll
// useGetFeedbackDraftStatus until it leaves "pending".
export const useGenerateFeedbackDraft = () =>
  useMutation({
    mutationFn: async (args: GenerateFeedbackDraftRequest) => {
      const res = await post<
        { data: GenerateFeedbackDraftResponse },
        GenerateFeedbackDraftRequest
      >("health-profiles/internal/icbt-feedback-drafts/generate", args);
      return res.data;
    },
  });

const DRAFT_STATUS_POLL_INTERVAL_MS = 2_500;

export const useGetFeedbackDraftStatus = (draftId: string | null) =>
  useQuery({
    queryKey: icbtKeys.feedbackDraftStatus(draftId),
    queryFn: async () => {
      const res = await get<{ data: FeedbackDraftStatusResponse }>(
        `health-profiles/internal/icbt-feedback-drafts/${draftId}/status`,
      );
      return res.data;
    },
    enabled: !!draftId,
    staleTime: 0,
    refetchInterval: (query) =>
      query.state.data?.status === "pending"
        ? DRAFT_STATUS_POLL_INTERVAL_MS
        : false,
  });

export const useGetAllFeedbackDraftsByRequestId = (
  args: GetAllFeedbackDraftsRequest,
  options?: { enabled?: boolean; refetchOnMount?: boolean | "always" },
) =>
  useQuery({
    queryKey: icbtKeys.feedbackDraftsByRequestId(args),
    queryFn: async () => {
      const res = await post<
        { data: GetAllFeedbackDraftsResponse },
        GetAllFeedbackDraftsRequest
      >(
        "health-profiles/internal/icbt-feedback-drafts/get-all-by-requestId",
        args,
      );
      return res.data;
    },
    staleTime: ICBT_STALE_TIME,
    ...options,
  });

export const useGetAllProgressForUsers = (
  userIds: string[],
  options?: { enabled?: boolean },
) =>
  useQuery({
    queryKey: icbtKeys.progressForUsers(userIds),
    queryFn: async () => {
      const results = await Promise.all(
        userIds.map((userId) =>
          post<ProgressByProgramId, { userId: string }>(
            "indianapolis/progress/internal",
            { userId },
          ),
        ),
      );
      return Object.fromEntries(
        userIds.map((userId, i) => [userId, results[i]]),
      ) as Record<string, ProgressByProgramId>;
    },
    enabled: userIds.length > 0 && (options?.enabled ?? true),
    staleTime: ICBT_STALE_TIME,
  });

export const useGetIcbtSequentialUnlock = (
  args: { userId: string },
  options?: {
    enabled?: boolean;
    refetchInterval?:
      | number
      | false
      | ((query: { state: { data: unknown } }) => number | false);
  },
) =>
  useQuery({
    queryKey: icbtKeys.sequentialUnlock(args.userId),
    queryFn: async () => {
      try {
        const res = await post<
          {
            data: Array<
              Omit<SequentialUnlockItem, "nextScheduledExecution"> & {
                nextScheduledExecution: string | null;
              }
            >;
          },
          typeof args
        >("health-profiles/internal/icbt-requests/sequential-unlock", args);
        return res.data.map((item) => ({
          ...item,
          nextScheduledExecution: item.nextScheduledExecution
            ? parseISO(item.nextScheduledExecution)
            : null,
        }));
      } catch (e) {
        if (isForbiddenError(e) || isErrorWithStatus(e, 404)) return [];
        throw e;
      }
    },
    staleTime: ICBT_STALE_TIME_SHORT,
    ...options,
  });

export const useGetAllIcbtCommunication = (
  userId: number | undefined,
  options?: { enabled?: boolean },
) =>
  useQuery({
    queryKey: icbtKeys.communication(userId),
    queryFn: async () => {
      try {
        const res = await post<
          { data: IcbtCommunication[] },
          { userId: number }
        >("health-profiles/internal/icbt-communication", { userId: userId! });
        return res.data.map((comm) => ({
          ...comm,
          feedbacks: comm.feedbacks.map((feedback) => ({
            ...feedback,
            feedback_status: "published" as const,
          })),
        }));
      } catch (e) {
        if (isForbiddenError(e) || isErrorWithStatus(e, 404)) return [];
        throw e;
      }
    },
    enabled: userId !== undefined && (options?.enabled ?? true),
    staleTime: ICBT_STALE_TIME,
  });

export const useGetPsychologistIcbtCommunication = (
  args: { startDate: Date; endDate: Date } | undefined,
  options?: { enabled?: boolean },
) =>
  useQuery({
    queryKey: icbtKeys.psychologistCommunication(args),
    queryFn: async () => {
      const res = await post<
        {
          data: {
            icbtCommunications: IcbtCommunication[];
            slots: PsychologistSlots[];
          };
        },
        typeof args
      >("health-profiles/internal/psychologist/icbt-communication", args);
      return {
        ...res.data,
        icbtCommunications: res.data.icbtCommunications.filter(
          (icbt) => icbt.feedback_date !== null,
        ),
      };
    },
    staleTime: ICBT_STALE_TIME,
    ...options,
  });

export const useGetPsychologistIcbtTasks = (
  args?: { status: "todo" | "done" | undefined },
  options?: { enabled?: boolean },
) =>
  useQuery({
    queryKey: icbtKeys.psychologistTasks(args),
    queryFn: async () => {
      const res = await post<
        {
          success: true;
          data: {
            icbtRequests: IcbtTasks[];
          };
        },
        typeof args
      >("health-profiles/internal/psychologist/tasks", args);
      return res.data.icbtRequests.filter((task) => task.feedbackDate !== null);
    },
    staleTime: ICBT_STALE_TIME,
    ...options,
  });

export const useGetNextSlotAndFeedback = (
  userId: string,
  options?: { enabled?: boolean },
) =>
  useQuery({
    queryKey: icbtKeys.nextSlotAndFeedback(userId),
    queryFn: async () => {
      const res = await post<
        { data: { slot: PsychologistSlots; icbtRequest: IcbtRequest } },
        { userId: string }
      >("health-profiles/internal/next-slot-and-feedback", { userId });
      return res.data;
    },
    staleTime: ICBT_STALE_TIME_SHORT,
    ...options,
  });

export const usePutIcbtFeedback = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (args: {
      userId: number;
      icbtRequestId: string;
      status: "draft" | "published";
      feedbackText: string;
    }) =>
      put<{ feedbackId: string }, typeof args>(
        "health-profiles/internal/icbt-feedback",
        args,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [ICBT_PSYCHOLOGIST_TASKS] });
      queryClient.invalidateQueries({ queryKey: [ICBT_FEEDBACKS] });
      queryClient.invalidateQueries({ queryKey: [ICBT_COMMUNICATION] });
      queryClient.invalidateQueries({
        queryKey: [ICBT_PSYCHOLOGIST_COMMUNICATION],
      });
      queryClient.invalidateQueries({ queryKey: [ICBT_REQUESTS] });
    },
  });
};

export const useDeleteIcbtFeedback = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (args: { userId: number; feedbackId: string }) =>
      del<{ feedbackId: string }, typeof args>(
        "health-profiles/internal/icbt-feedback",
        args,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [ICBT_FEEDBACKS] });
      queryClient.invalidateQueries({ queryKey: [ICBT_COMMUNICATION] });
      queryClient.invalidateQueries({
        queryKey: [ICBT_PSYCHOLOGIST_COMMUNICATION],
      });
      queryClient.invalidateQueries({ queryKey: [ICBT_REQUESTS] });
    },
  });
};

export const usePutSortIcbtRequests = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (args: {
      userId: number;
      sortedRequestIds: number[];
      initiatedByUserId: number;
    }) =>
      put<
        void,
        { userId: number; requestData: number[]; initiatedByUserId: number }
      >("health-profiles/internal/sort_icbt_requests", {
        userId: args.userId,
        requestData: args.sortedRequestIds,
        initiatedByUserId: args.initiatedByUserId,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [ICBT_REQUESTS] });
    },
  });
};

export const usePutIcbtRequestFeedbackDate = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (args: {
      userId: number;
      icbtRequestId: string;
      feedbackDate: Date;
    }) =>
      put<IcbtCommunication[], { userId: number; feedbackDate: Date }>(
        `health-profiles/internal/icbt_requests/${args.icbtRequestId}`,
        { userId: args.userId, feedbackDate: args.feedbackDate },
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [ICBT_PSYCHOLOGIST_TASKS] });
      queryClient.invalidateQueries({ queryKey: [ICBT_REQUESTS] });
      queryClient.invalidateQueries({ queryKey: [ICBT_COMMUNICATION] });
      queryClient.invalidateQueries({
        queryKey: [ICBT_PSYCHOLOGIST_COMMUNICATION],
      });
    },
  });
};

export const useSetIcbtRequestFeedbackDateIgnored = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (args: {
      mode: "IGNORE" | "CEASE_IGNORE";
      icbtRequestId: string;
    }) =>
      put<void, { mode: string }>(
        `health-profiles/internal/icbt_requests_ignored_at/${args.icbtRequestId}`,
        { mode: args.mode },
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [ICBT_REQUESTS] });
      queryClient.invalidateQueries({ queryKey: [ICBT_COMMUNICATION] });
      queryClient.invalidateQueries({ queryKey: [ICBT_PSYCHOLOGIST_TASKS] });
      queryClient.invalidateQueries({
        queryKey: [ICBT_PSYCHOLOGIST_COMMUNICATION],
      });
    },
  });
};

type PostIcbtRequestBody = {
  userId: number;
  programId: string;
  treatmentId: string;
  feedbackDate?: Date;
};

type PostIcbtRequestResult =
  | {
      success: true;
      data: Array<{
        id: string;
        patient_seen_at: string | null;
        program_id: string;
        request_order: number;
        requested_at: string;
        requesting_psychologist_id: string;
        treatment_id: string;
        user_id: string;
      }>;
    }
  | {
      success: false;
      error: string;
    };

export const usePostIcbtRequests = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      userId,
      body,
    }: {
      userId: number;
      body: PostIcbtRequestBody[];
    }) => {
      const response = await post<
        { data: PostIcbtRequestResult },
        PostIcbtRequestBody[]
      >(`privacy/data-collection/icbt_requests/${userId}`, body);
      return response.data;
    },
    onMutate: async ({ userId }) => {
      const progressKey = icbtKeys.progress(userId.toString());
      await queryClient.cancelQueries({ queryKey: progressKey });
      const previousProgress =
        queryClient.getQueryData<ProgressByProgramId>(progressKey);
      return { previousProgress };
    },
    onSuccess: (data, { userId }, context) => {
      if (!data.success) {
        logger.error(
          "Failed to post ICBT request, returned non-success status, not able to update cache optimistically",
          { error: data.error },
        );
        queryClient.setQueryData(
          icbtKeys.progress(userId.toString()),
          context?.previousProgress,
        );
        return;
      }

      const newRequests = data.data;

      queryClient.setQueryData<ProgressByProgramId>(
        icbtKeys.progress(userId.toString()),
        (currentProgresses = {}) => {
          const updated = { ...currentProgresses };
          newRequests.forEach((request) => {
            const currentProgress = currentProgresses[request.program_id];
            const emptyProgressBase: Progress = {
              currentPageIndex: 0,
              completed: false,
              programId: request.program_id,
              assignId: request.id,
              maxProgressedPageIndex: 0,
            };
            let newArchive = currentProgress?.archivedProgress ?? [];
            if (currentProgress) {
              const { archivedProgress: _archived, ...progressWithoutArchive } =
                currentProgress;
              newArchive = [...newArchive, progressWithoutArchive];
            }
            updated[request.program_id] = {
              ...emptyProgressBase,
              archivedProgress: newArchive,
            };
          });
          return updated;
        },
      );
    },
    onError: (_err, { userId }, context) => {
      queryClient.setQueryData(
        icbtKeys.progress(userId.toString()),
        context?.previousProgress,
      );
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: [ICBT_REQUESTS] });
      queryClient.invalidateQueries({ queryKey: [ICBT_SEQUENTIAL_UNLOCK] });
      queryClient.invalidateQueries({ queryKey: [ICBT_PROGRESS] });
    },
  });
};

export const useRemoveIcbtRequest = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      requestId,
      userId,
    }: {
      requestId: string;
      userId: string;
      programId: string;
    }) =>
      del<void, { userId: string }>(
        `privacy/data-collection/icbt_requests/${requestId}`,
        { userId },
      ),
    onMutate: async ({ requestId, userId, programId }) => {
      const progressKey = icbtKeys.progress(userId);
      const requestsKey = icbtKeys.requests(Number(userId));

      await queryClient.cancelQueries({ queryKey: progressKey });
      await queryClient.cancelQueries({ queryKey: requestsKey });

      const previousProgress =
        queryClient.getQueryData<ProgressByProgramId>(progressKey);

      // Optimistically remove the request so the row disappears immediately
      queryClient.setQueryData<IcbtRequest[]>(requestsKey, (current) =>
        current?.filter((r) => r.id !== requestId),
      );

      queryClient.setQueryData<ProgressByProgramId>(
        progressKey,
        (current = {}) => {
          const currentProgress = current[programId];
          if (!currentProgress) return current;

          const currentArchive = currentProgress.archivedProgress ?? [];
          const latestFromArchive = currentArchive[currentArchive.length - 1];

          if (!latestFromArchive) {
            const { [programId]: _removed, ...rest } = current;
            return rest;
          }

          return {
            ...current,
            [programId]: {
              ...latestFromArchive,
              archivedProgress: currentArchive.slice(0, -1),
            },
          };
        },
      );

      return { previousProgress };
    },
    onError: (_err, { userId }, context) => {
      queryClient.setQueryData(
        icbtKeys.progress(userId),
        context?.previousProgress,
      );
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: [ICBT_REQUESTS] });
      queryClient.invalidateQueries({ queryKey: [ICBT_PROGRESS] });
    },
  });
};

export const useIcbtCommunicationFeatureEnabledForPatient = (
  patientUserId: number,
) => {
  const countryCode = getCountryCode();
  const {
    data: icbtClaims,
    isFetching,
    error,
  } = useGetIcbtClaims({
    userId: patientUserId,
    marketISO2: countryCode,
  });
  return {
    data: {
      hasIcbtResponseFeatureEnabled: icbtClaims?.hasIcbtResponseFeatureEnabled,
      hasIcbtResponseFeatureEnabledInExternalSystem:
        icbtClaims?.hasIcbtResponseFeatureEnabledInExternalSystem,
    },
    isFetching,
    error,
  };
};

export const useAllowedToViewPatientIcbt = (patientUserId: number) => {
  const { error } = useGetIcbtRequests(patientUserId);
  const forbiddenError = error && isForbiddenError(error);
  return !forbiddenError;
};

export const useGetIcbtAssessment = (
  args: { icbtRequestId: string },
  options?: { enabled?: boolean },
) =>
  useQuery({
    queryKey: icbtKeys.assessment(args),
    queryFn: async () => {
      try {
        const res = await post<
          { data: IcbtAssessment },
          { icbtRequestId: string }
        >("health-profiles/internal/icbt-assessment", {
          icbtRequestId: args.icbtRequestId,
        });
        return res.data;
      } catch (e) {
        if (isErrorWithStatus(e, 404)) return undefined;
        throw e;
      }
    },
    staleTime: Infinity,
    ...options,
  });

export const usePutIcbtAssessment = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (args: {
      icbtRequestId: string;
      publish?: boolean;
      assessmentText: string | null;
    }) =>
      put<{ data: PutIcbtAssessmentResponse }, typeof args>(
        "health-profiles/internal/icbt-assessment",
        args,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [ICBT_ASSESSMENT] });
      // Publishing changes assessment.status/actions on the sequential unlock
      queryClient.invalidateQueries({ queryKey: [ICBT_SEQUENTIAL_UNLOCK] });
      queryClient.invalidateQueries({ queryKey: [ICBT_PSYCHOLOGIST_TASKS] });
    },
  });
};
